import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { useIsMobile } from "@/ui/foundation/hooks/use-mobile"
import { cn } from "@/ui/foundation/lib/utils"
import { Button } from "@/ui/foundation/primitives/button"
import { Input } from "@/ui/foundation/primitives/input"
import { Separator } from "@/ui/foundation/primitives/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/foundation/primitives/sheet"
import { Skeleton } from "@/ui/foundation/primitives/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/ui/foundation/primitives/tooltip"
import { PanelLeftIcon } from "lucide-react"

const SHELL_COOKIE_NAME = "shell_state"
const SHELL_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
const SHELL_PANE_WIDTH = "22rem"
const SHELL_PANE_WIDTH_MOBILE = "18rem"
const SHELL_PANE_WIDTH_ICON = "5.5rem"
const SHELL_KEYBOARD_SHORTCUT = "b"

type ShellContextProps = {
  state: "expanded" | "collapsed"
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleShellPane: () => void
}

const ShellContext = React.createContext<ShellContextProps | null>(null)

function useShell() {
  const context = React.useContext(ShellContext)
  if (!context) {
    throw new Error("useShell must be used within a ShellProvider.")
  }

  return context
}

function ShellProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const isMobile = useIsMobile()
  const [openMobile, setOpenMobile] = React.useState(false)

  // This is the internal state of the shell.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(openState)
      } else {
        _setOpen(openState)
      }

      // This sets the cookie to keep the shell state.
      document.cookie = `${SHELL_COOKIE_NAME}=${openState}; path=/; max-age=${SHELL_COOKIE_MAX_AGE}`
    },
    [setOpenProp, open]
  )

  // Helper to toggle the shell.
  const toggleShellPane = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open)
  }, [isMobile, setOpen, setOpenMobile])

  // Adds a keyboard shortcut to toggle the shell.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SHELL_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault()
        toggleShellPane()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggleShellPane])

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the shell with Tailwind classes.
  const state = open ? "expanded" : "collapsed"

  const contextValue = React.useMemo<ShellContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleShellPane,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleShellPane]
  )

  return (
    <ShellContext.Provider value={contextValue}>
      <div
        data-slot="shell-wrapper"
        style={
          {
            "--manager-secondary-pane-width": SHELL_PANE_WIDTH,
            "--manager-nav-rail-width": SHELL_PANE_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          "group/shell-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-pane",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </ShellContext.Provider>
  )
}

function ShellPane({
  side = "left",
  variant = "shell",
  collapsible = "offcanvas",
  className,
  children,
  dir,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right"
  variant?: "shell" | "floating" | "inset"
  collapsible?: "offcanvas" | "icon" | "none"
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useShell()

  if (collapsible === "none") {
    return (
      <div
        data-slot="shell"
        className={cn(
          "flex h-full w-(--manager-secondary-pane-width) flex-col bg-pane text-pane-foreground",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          dir={dir}
          data-shell="shell"
          data-slot="shell"
          data-mobile="true"
          className="w-(--manager-secondary-pane-width) bg-pane p-0 text-pane-foreground [&>button]:hidden"
          style={
            {
              "--manager-secondary-pane-width": SHELL_PANE_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>ShellPane</SheetTitle>
            <SheetDescription>Displays the mobile shell.</SheetDescription>
          </SheetHeader>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <div
      className="group peer hidden text-pane-foreground md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="shell"
    >
      {/* This is what handles the shell gap on desktop */}
      <div
        data-slot="shell-gap"
        className={cn(
          "relative w-(--manager-secondary-pane-width) bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--manager-nav-rail-width)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--manager-nav-rail-width)"
        )}
      />
      <div
        data-slot="shell-container"
        data-side={side}
        className={cn(
          "fixed inset-y-0 z-10 hidden h-svh w-(--manager-secondary-pane-width) transition-[left,right,width] duration-200 ease-linear data-[side=left]:left-0 data-[side=left]:group-data-[collapsible=offcanvas]:left-[calc(var(--manager-secondary-pane-width)*-1)] data-[side=right]:right-0 data-[side=right]:group-data-[collapsible=offcanvas]:right-[calc(var(--manager-secondary-pane-width)*-1)] md:flex",
          // Adjust the padding for floating and inset variants.
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--manager-nav-rail-width)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--manager-nav-rail-width) group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className
        )}
        {...props}
      >
        <div
          data-shell="shell"
          data-slot="shell-inner"
          className="flex size-full flex-col bg-pane group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:ring-1 group-data-[variant=floating]:ring-pane-border"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function ShellTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleShellPane } = useShell()

  return (
    <Button
      data-shell="trigger"
      data-slot="shell-trigger"
      variant="ghost"
      size="icon-sm"
      className={cn(className)}
      onClick={(event) => {
        onClick?.(event)
        toggleShellPane()
      }}
      {...props}
    >
      <PanelLeftIcon />
      <span className="sr-only">Toggle ShellPane</span>
    </Button>
  )
}

function ShellRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleShellPane } = useShell()

  return (
    <button
      data-shell="rail"
      data-slot="shell-rail"
      aria-label="Toggle ShellPane"
      tabIndex={-1}
      onClick={toggleShellPane}
      title="Toggle ShellPane"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:start-1/2 after:w-[2px] hover:after:bg-pane-border sm:flex ltr:-translate-x-1/2 rtl:-translate-x-1/2",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-pane",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className
      )}
      {...props}
    />
  )
}

function ShellMain({ className, ...props }: React.ComponentProps<"main">) {
  return (
    <main
      data-slot="shell-inset"
      className={cn(
        "relative flex w-full flex-1 flex-col bg-background md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className
      )}
      {...props}
    />
  )
}

function PaneInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="shell-input"
      data-shell="input"
      className={cn("h-8 w-full bg-background shadow-none", className)}
      {...props}
    />
  )
}

function PaneHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-header"
      data-shell="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function PaneFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-footer"
      data-shell="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

function PaneSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="shell-separator"
      data-shell="separator"
      className={cn("mx-2 w-auto bg-pane-border", className)}
      {...props}
    />
  )
}

function PaneContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-content"
      data-shell="content"
      className={cn(
        "no-scrollbar flex min-h-0 flex-1 flex-col gap-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

function PaneGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-group"
      data-shell="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...props}
    />
  )
}

function PaneGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "div"

  return (
    <Comp
      data-slot="shell-group-label"
      data-shell="group-label"
      className={cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-pane-foreground/70 ring-pane-ring outline-hidden transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        className
      )}
      {...props}
    />
  )
}

function PaneGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="shell-group-action"
      data-shell="group-action"
      className={cn(
        "absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-pane-foreground ring-pane-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden after:absolute after:-inset-2 hover:bg-pane-accent hover:text-pane-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
        className
      )}
      {...props}
    />
  )
}

function PaneGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-group-content"
      data-shell="group-content"
      className={cn("w-full text-sm", className)}
      {...props}
    />
  )
}

function PaneMenu({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="shell-menu"
      data-shell="menu"
      className={cn("flex w-full min-w-0 flex-col gap-0", className)}
      {...props}
    />
  )
}

function PaneMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="shell-menu-item"
      data-shell="menu-item"
      className={cn("group/menu-item relative", className)}
      {...props}
    />
  )
}

const paneMenuButtonVariants = cva(
  "peer/menu-button group/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm ring-pane-ring outline-hidden transition-[width,height,padding] group-has-data-[shell=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! hover:bg-pane-accent hover:text-pane-accent-foreground focus-visible:ring-2 active:bg-pane-accent active:text-pane-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-open:hover:bg-pane-accent data-open:hover:text-pane-accent-foreground data-active:bg-pane-accent data-active:font-medium data-active:text-pane-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate",
  {
    variants: {
      variant: {
        default: "hover:bg-pane-accent hover:text-pane-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--pane-border))] hover:bg-pane-accent hover:text-pane-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--pane-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function PaneMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  isActive?: boolean
  tooltip?: string | React.ComponentProps<typeof TooltipContent>
} & VariantProps<typeof paneMenuButtonVariants>) {
  const Comp = asChild ? Slot.Root : "button"
  const { isMobile, state } = useShell()

  const button = (
    <Comp
      data-slot="shell-menu-button"
      data-shell="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(paneMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  )

  if (!tooltip) {
    return button
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  )
}

function PaneMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean
  showOnHover?: boolean
}) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="shell-menu-action"
      data-shell="menu-action"
      className={cn(
        "absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-pane-foreground ring-pane-ring outline-hidden transition-transform group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-pane-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 after:absolute after:-inset-2 hover:bg-pane-accent hover:text-pane-accent-foreground focus-visible:ring-2 md:after:hidden [&>svg]:size-4 [&>svg]:shrink-0",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-active/menu-button:text-pane-accent-foreground aria-expanded:opacity-100 md:opacity-0",
        className
      )}
      {...props}
    />
  )
}

function PaneMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="shell-menu-badge"
      data-shell="menu-badge"
      className={cn(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-pane-foreground tabular-nums select-none group-data-[collapsible=icon]:hidden peer-hover/menu-button:text-pane-accent-foreground peer-data-[size=default]/menu-button:top-1.5 peer-data-[size=lg]/menu-button:top-2.5 peer-data-[size=sm]/menu-button:top-1 peer-data-active/menu-button:text-pane-accent-foreground",
        className
      )}
      {...props}
    />
  )
}

function PaneMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean
}) {
  // Random width between 50 to 90%.
  const [width] = React.useState(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`
  })

  return (
    <div
      data-slot="shell-menu-skeleton"
      data-shell="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-shell="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-shell="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  )
}

function PaneMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="shell-menu-sub"
      data-shell="menu-sub"
      className={cn(
        "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-pane-border px-2.5 py-0.5 group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
}

function PaneMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="shell-menu-sub-item"
      data-shell="menu-sub-item"
      className={cn("group/menu-sub-item relative", className)}
      {...props}
    />
  )
}

function PaneMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean
  size?: "sm" | "md"
  isActive?: boolean
}) {
  const Comp = asChild ? Slot.Root : "a"

  return (
    <Comp
      data-slot="shell-menu-sub-button"
      data-shell="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-pane-foreground ring-pane-ring outline-hidden group-data-[collapsible=icon]:hidden hover:bg-pane-accent hover:text-pane-accent-foreground focus-visible:ring-2 active:bg-pane-accent active:text-pane-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[size=md]:text-sm data-[size=sm]:text-xs data-active:bg-pane-accent data-active:text-pane-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-pane-accent-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  ShellPane,
  PaneContent,
  PaneFooter,
  PaneGroup,
  PaneGroupAction,
  PaneGroupContent,
  PaneGroupLabel,
  PaneHeader,
  PaneInput,
  ShellMain,
  PaneMenu,
  PaneMenuAction,
  PaneMenuBadge,
  PaneMenuButton,
  PaneMenuItem,
  PaneMenuSkeleton,
  PaneMenuSub,
  PaneMenuSubButton,
  PaneMenuSubItem,
  ShellProvider,
  ShellRail,
  PaneSeparator,
  ShellTrigger,
  useShell,
}
